// implementing vuex state management

import Vue from "vue";
import Vuex from "vuex";
import ProductCatalogProvider from "../providers/ProductCatalogProvider";
import LandingpageDataProvider from "../providers/LandingpageDataProvider";
import PriceRulesProvider from "../providers/PriceRulesProvider";
import MyStore from "./MyStore";
import ServerDataProvider from "../providers/ServerDataProvider";
import FeatureToggleProvider from "../providers/FeatureToggleProvider";
import ABTestingProvider from "../providers/ABTestingProvider";
import GlobalVersionProvider from "../providers/GlobalVersionProvider";
import {flowSetting} from "../libraries/utils";
import * as mutationTypes from "./mutation-types";
import appConfig, {channelEnum} from "../appconfig";

import mutationTypesTid2 from "./tenantSpecificModules/storeModulesTid2/mutationTypesTid2";

Vue.use(Vuex);

export let store = null;

/**
 * Returns the store to be used by this application.
 * As we have unittest errors if the store is initialized during module load we will initialize it lazily.
 * @returns {*}
 * @constructor
 */
const createStore = async () => {
	if (store) {
		switch(location.pathname) {
		case "/":
		case "/shop-selection":
			store.dispatch("resetGlobalSelectedShop");
			store.commit(mutationTypes.SET_TENANT_SPECIFIC_DATA, {
				key: "deliveryChoice"
			});
			break;
		case "/delivery-method":
			store.commit(mutationTypes.SET_TENANT_SPECIFIC_DATA, {
				key: "deliveryChoice"
			});
			break;
		}
		return store;
	} else {
		const storedUseApiCdnCaching = JSON.parse(localStorage.getItem("useApiCdnCaching"));
		const storedUseLandingpageEndpoint = JSON.parse(localStorage.getItem("useLandingpageEndpoint"));

		const [serverData, featureToggles, priceRules, shops, globalVersions, sallingHolidays, clientRedirects, abTests] = await (async () => {
			// Have a fallback if the CDN caching is not working for some reason, so we can still pick up the fresh value
			// of the feature toggles (CDN caching may have been turned off but we can't pick it up due to the error)

			const fetchPriceRules = () => {
				if (appConfig.isBM100()) {
					return false;
				}
				return true;
			};

			const getLoadPromise = (useCdnCache) => {
				const promises = [
					ServerDataProvider.getServerData(useCdnCache),
					FeatureToggleProvider.getFeatureTogglesAsPromise(!useCdnCache, useCdnCache),
					fetchPriceRules() ? PriceRulesProvider.findAll({}, useCdnCache) : undefined,
					undefined,
					GlobalVersionProvider.findAll(useCdnCache),
					undefined,
					undefined
				];

				// Conditionally add the ABTestingProvider.getABTests() promise if appConfig.isBM100() is true
				if (appConfig.isBM100()) {
					promises.push(ABTestingProvider.getABTests(useCdnCache));
				}

				return Promise.all(promises);
			};

			const getLoadPromiseForLandingpage = (useCdnCache) => Promise.all([
				LandingpageDataProvider.getCombinedDataApi(useCdnCache),
				FeatureToggleProvider.getFeatureTogglesAsPromise(false, false, storedUseLandingpageEndpoint)
			]);
			// FeatureToggleProvider is added here to block redundant calls

			try {
				if (storedUseLandingpageEndpoint === true && !flowSetting.isFlow("admin")) {
					const [landingpageData] = await getLoadPromiseForLandingpage(storedUseApiCdnCaching);

					return [
						landingpageData.monitoring,
						landingpageData.featureToggles,
						landingpageData.priceRules,
						landingpageData.shops,
						landingpageData.globalVersions,
						landingpageData.holidays,
						landingpageData.clientRedirects,
						{}
					];
				}
				return await getLoadPromise(storedUseApiCdnCaching);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err);
				return getLoadPromise(false);
			}
		})();

		if(window.parent) window.parent.postMessage({ type: "SERVER_DATA", payload: { ...serverData, featureToggles } }, "*");

		const useNewProductCatalog = (featureToggles.useNewProductCatalog || {}).value || false;
		const useApiCdnCaching = (featureToggles.useApiCdnCaching || {}).value || false;
		const useNewShopsData = (featureToggles.useNewShopsData || {}).value || false;
		const useLandingpageEndpoint = (featureToggles.useLandingpageEndpoint || {}).value || false;
		const useNestOrderPaymentFlow =  (featureToggles.useNestOrderPaymentFlow || {}).value || false;

		localStorage.setItem("useApiCdnCaching", JSON.stringify(useApiCdnCaching));
		localStorage.setItem("useLandingpageEndpoint", JSON.stringify(useLandingpageEndpoint));

		// TODO - getting product catalog data on this level should be removed!!!
		let enrichedProductCatalogData = {
			products: [],
			categories: [],
			departments: []
		};

		const isKiosk = appConfig.channelAlias() === channelEnum.kiosk;
		const isSG = appConfig.getBusinessModelId() === appConfig.businessModelsEnum.bm2.id;

		if (!useNewShopsData || flowSetting.isFlow("admin")) {
			try {
				enrichedProductCatalogData = await ProductCatalogProvider
					.getProductCatalogData({ useNewProductCatalog, useApiCdnCaching, useNestOrderPaymentFlow });
			} catch (e) {
				// TODO: do something about it
			}
		} else if (useNewProductCatalog && !(isKiosk && window.location.pathname === "/")) { // don't fetch product catalog data on the landing page
			const vuexStore = JSON.parse(localStorage.getItem("vuex"));

			if (vuexStore) {
				const shopStore = vuexStore ? vuexStore.shop : null;
				const shopState = shopStore ? shopStore.persistedData : null;
				const globalSelectedShop = shopState ? shopState.globalSelectedShop : null;
				const globalSelectedShopId = globalSelectedShop ? globalSelectedShop.id : null;
				const cartStore = vuexStore ? vuexStore.cart : null;
				const cartState = cartStore ? cartStore.persistedData : null;
				const cartData = (cartState && cartState.orderEditMode)
					? cartState.editModeData
					: cartState.newCartData;
				const tenantSpecificData = cartData ? cartData.tenantSpecificData : null;
				const deliveryChoice = tenantSpecificData ? tenantSpecificData.deliveryChoice : null;

				if (isSG || (isKiosk) || (!isKiosk && (!window.isNaN(deliveryChoice) || globalSelectedShopId))) {
					enrichedProductCatalogData = await ProductCatalogProvider
						.getProductCatalogData({ useNewProductCatalog, useApiCdnCaching, shopId: globalSelectedShopId, deliveryMethod: deliveryChoice, useNestOrderPaymentFlow });
				}
			} else {
				if (isSG) {
					enrichedProductCatalogData = await ProductCatalogProvider.getProductCatalogData({ useNewProductCatalog, useApiCdnCaching, useNestOrderPaymentFlow });
				}
			}
		} else {
			if (isSG) {
				enrichedProductCatalogData = await ProductCatalogProvider.getProductCatalogData({ useNewProductCatalog, useApiCdnCaching, useNestOrderPaymentFlow });
			}
		}

		store = await MyStore({
			enrichedProductCatalogData, // it will not be set for kiosk initially, must leave it for other channels (mobile) since it is used by other tenants as pure product catalog
			serverData,
			featureToggles,
			priceRules: priceRules && priceRules.data,
			globalVersions: globalVersions.data
		});

		await store.dispatch("cacheServerData", {});

		if (abTests){
			store.dispatch("startABTesting", abTests);
		}

		if (storedUseLandingpageEndpoint === true && !flowSetting.isFlow("admin")) {
			// TODO Expiremental! Find the proper way to cache SG holidays and shops
			store.commit(mutationTypesTid2.SET_SG_HOLIDAYS, sallingHolidays);
			store.commit(mutationTypes.SET_CLIENT_REDIRECTS_DATA, clientRedirects);
			store.commit(mutationTypes.SET_CACHED_SHOPS, shops);
		}

		return store;
	}
};
export default createStore;
